import { useRef, useState } from 'react';
import { Upload, Link as LinkIcon, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

/**
 * Field combinado para subir imagen (archivo) o pegar URL.
 * value: string (url actual)
 * onChange: (url: string) => void
 * sizeHint: string
 */
export default function ImageUploadOrUrl({ value, onChange, sizeHint, testid = 'image-upload-or-url' }) {
  const [mode, setMode] = useState('upload');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) { toast.error('Solo imágenes'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (data?.url) onChange?.(data.url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo subir');
    } finally { setUploading(false); }
  };

  return (
    <div data-testid={testid}>
      <div className="mb-2 flex items-center gap-2">
        <button type="button" onClick={() => setMode('upload')} className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold ${mode === 'upload' ? 'bg-cnib-blue text-white' : 'bg-slate-100 text-slate-600'}`} data-testid={`${testid}-mode-upload`}>
          <Upload className="h-3 w-3" /> Subir
        </button>
        <button type="button" onClick={() => setMode('url')} className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold ${mode === 'url' ? 'bg-cnib-blue text-white' : 'bg-slate-100 text-slate-600'}`} data-testid={`${testid}-mode-url`}>
          <LinkIcon className="h-3 w-3" /> URL
        </button>
        {sizeHint && <span className="ml-auto text-[11px] text-slate-500">Recomendado: {sizeHint}</span>}
      </div>

      {mode === 'upload' ? (
        <div>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files?.[0])} />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 py-6 text-sm font-bold text-slate-500 hover:bg-slate-100 disabled:opacity-60" data-testid={`${testid}-button`}>
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {uploading ? 'Subiendo…' : (value ? 'Reemplazar imagen' : 'Seleccionar imagen')}
          </button>
        </div>
      ) : (
        <input value={value || ''} onChange={(e) => onChange?.(e.target.value)} placeholder="https://..." className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-cnib-blue focus:ring-2 focus:ring-cnib-blue/15" data-testid={`${testid}-url-input`} />
      )}

      {value && (
        <div className="mt-2 relative overflow-hidden rounded-xl border border-slate-200">
          <img src={value} alt="" className="h-28 w-full object-cover" />
          <button type="button" onClick={() => onChange?.('')} className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-slate-900/70 text-white" data-testid={`${testid}-clear`}>
            <X className="h-3 w-3" />
          </button>
        </div>
      )}
    </div>
  );
}
